<template>
  <v-container fluid class="itinerary-analysis-tab pa-4 pa-md-6">
    <!-- header -->
    <div class="d-flex align-center flex-wrap mb-6">
      <div>
        <div class="d-flex align-center">
          <h2 class="title font-weight-medium mr-3">Itinerary Analysis</h2>
        </div>
        <div class="body-2 grey--text text--darken-1 mt-1">
          Structural and pricing checks for the saved itinerary. Fixes open in
          the builder; only times can be set from here.
        </div>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center mt-2 mt-sm-0">
        <span
          v-if="analysis && analysis.createdAt"
          class="grey--text caption mr-4"
        >
          Last run {{ formatDateTime(analysis.createdAt) }}
        </span>
        <v-btn
          color="primary"
          depressed
          large
          class="px-6 analyse-btn"
          :loading="inProgress || starting"
          :disabled="inProgress || starting"
          @click="analyse()"
        >
          <v-icon left>mdi-radar</v-icon>Analyse
        </v-btn>
      </div>
    </div>

    <v-alert
      v-if="requestMessage"
      dense
      outlined
      :type="requestMessage.type"
      class="mb-6"
      dismissible
      @input="requestMessage = null"
    >
      {{ requestMessage.text }}
    </v-alert>

    <!-- loading the last run -->
    <v-progress-linear
      v-if="!analysis && $apollo.queries.analysis.loading"
      indeterminate
      color="primary"
      rounded
      height="4"
    ></v-progress-linear>

    <!-- empty state -->
    <v-card v-else-if="!analysis" outlined class="soft-card pa-10 text-center">
      <v-avatar size="72" color="primary lighten-5" class="mb-4">
        <v-icon size="36" color="primary">mdi-radar</v-icon>
      </v-avatar>
      <div class="title font-weight-medium">No analysis yet</div>
      <div
        class="body-1 grey--text text--darken-1 mx-auto mt-2"
        style="max-width: 520px"
      >
        Click <strong>Analyse</strong> to check this itinerary for gap nights,
        overlapping stays, missing transfers, time clashes, pax mismatches, weak
        margins and cheaper local alternatives.
      </div>
      <div class="mt-6 d-flex justify-center flex-wrap">
        <v-chip
          v-for="c in checkChips"
          :key="c.label"
          outlined
          class="ma-1"
          :color="c.color"
        >
          <v-icon small left>{{ c.icon }}</v-icon
          >{{ c.label }}
        </v-chip>
      </div>
    </v-card>

    <!-- in progress -->
    <v-card v-else-if="inProgress" outlined class="soft-card pa-8">
      <div class="d-flex align-center">
        <v-progress-circular
          indeterminate
          size="40"
          width="3"
          color="primary"
          class="mr-5"
        ></v-progress-circular>
        <div>
          <div class="title font-weight-medium">Analysing…</div>
        </div>
      </div>
      <v-progress-linear
        indeterminate
        color="primary"
        rounded
        height="4"
        class="mt-6"
      ></v-progress-linear>
    </v-card>

    <!-- error -->
    <v-card
      v-else-if="analysis.status === 'error'"
      outlined
      class="soft-card pa-6 error-card"
    >
      <div class="d-flex align-start">
        <v-avatar size="44" color="red lighten-5" class="mr-4">
          <v-icon color="error">mdi-alert-octagon</v-icon>
        </v-avatar>
        <div>
          <div class="title font-weight-medium error--text">
            Analysis failed
          </div>
          <div class="body-1 mt-1">
            {{ analysis.error || "The analysis could not be completed." }}
          </div>
          <div class="caption grey--text mt-2">
            Run {{ analysis.id }} · {{ formatDateTime(analysis.finishedAt) }}
          </div>
        </div>
        <v-spacer></v-spacer>
        <v-btn text color="primary" @click="analyse()">Retry</v-btn>
      </div>
    </v-card>

    <!-- results -->
    <template v-else>
      <!-- summary -->
      <v-card outlined class="soft-card pa-6 mb-6">
        <div class="d-flex align-center flex-wrap">
          <v-avatar
            size="52"
            :color="severityColor(analysis.severity) + ' lighten-5'"
            class="mr-4"
          >
            <v-icon :color="severityColor(analysis.severity)" size="28">
              {{ severityIcon(analysis.severity) }}
            </v-icon>
          </v-avatar>
          <div>
            <div
              v-if="narrative"
              class="subtitle-1 font-weight-medium narrative-summary"
            >
              <v-chip
                x-small
                label
                color="primary lighten-5"
                class="primary--text mr-2"
                :title="'Written by ' + analysis.stats.narrativeModel"
              >
                <v-icon x-small left>mdi-creation</v-icon>AI
              </v-chip>
              {{ analysis.summary }}
            </div>
            <div v-else class="headline font-weight-medium">
              {{ analysis.summary }}
            </div>
          </div>
        </div>

        <v-row class="mt-5" dense>
          <v-col cols="12" sm="4" v-for="s in severities" :key="s">
            <div
              class="stat-tile pa-4"
              :class="[
                'stat-tile--' + s,
                severityFilter.includes(s) ? 'stat-tile--on' : 'stat-tile--off',
              ]"
              @click="toggleSeverity(s)"
            >
              <div class="d-flex align-center">
                <v-icon :color="severityColor(s)" class="mr-2">
                  {{ severityIcon(s) }}
                </v-icon>
                <span class="display-1 font-weight-medium">{{
                  counts[s]
                }}</span>
                <v-spacer></v-spacer>
                <v-icon
                  small
                  :color="
                    severityFilter.includes(s) ? severityColor(s) : 'grey'
                  "
                >
                  {{
                    severityFilter.includes(s)
                      ? "mdi-eye"
                      : "mdi-eye-off-outline"
                  }}
                </v-icon>
              </div>
              <div class="body-2 mt-1 grey--text text--darken-2">
                {{ severityLabel(s) }}
              </div>
            </div>
          </v-col>
        </v-row>

        <div class="d-flex align-center flex-wrap mt-5">
          <span class="caption grey--text text-uppercase mr-3">Sections</span>
          <v-chip-group
            v-model="sectionFilter"
            multiple
            active-class="primary--text"
          >
            <v-chip
              v-for="s in sectionsPresent"
              :key="s"
              :value="s"
              small
              filter
              outlined
            >
              <v-icon small left>{{ sectionIcon(s) }}</v-icon>
              <span class="text-capitalize">{{ s }}</span>
            </v-chip>
          </v-chip-group>
        </div>
      </v-card>

      <!-- AI recommendations (ITA-21) -->
      <v-card
        v-if="recommendations.length"
        outlined
        class="soft-card pa-6 mb-6"
      >
        <div class="d-flex align-center mb-3">
          <v-icon small color="primary" class="mr-2">mdi-creation</v-icon>
          <span class="subtitle-1 font-weight-medium">Recommendations</span>
        </div>
        <ol class="recommendations">
          <li v-for="(r, i) in recommendations" :key="i" class="body-1 mb-2">
            {{ r.text }}
            <span
              v-for="id in r.findingIds"
              :key="id"
              class="finding-link caption ml-1"
              :title="findingTitle(id)"
              @click="openFinding(id)"
              >{{ findingTitle(id) }}</span
            >
          </li>
        </ol>
      </v-card>

      <!-- findings -->
      <div v-for="group in groupedFindings" :key="group.severity" class="mb-8">
        <div class="d-flex align-center mb-3">
          <v-icon small :color="severityColor(group.severity)" class="mr-2">
            {{ severityIcon(group.severity) }}
          </v-icon>
          <span class="subtitle-1 font-weight-medium">
            {{ severityLabel(group.severity) }}
          </span>
          <span class="grey--text ml-2">{{ group.items.length }}</span>
        </div>

        <v-card
          v-for="item in group.items"
          :key="item.id"
          :id="'finding-' + item.id"
          outlined
          class="soft-card finding mb-3"
          :class="[
            'finding--' + item.severity,
            isExpanded(item) ? 'finding--open' : '',
          ]"
        >
          <div
            class="d-flex align-start pa-5 finding__head"
            @click="toggleRow(item)"
          >
            <div class="flex-grow-1 min-w-0">
              <div
                class="
                  d-flex
                  align-center
                  flex-wrap
                  caption
                  grey--text
                  text--darken-1
                  mb-1
                "
              >
                <v-icon x-small class="mr-1">{{
                  sectionIcon(item.section)
                }}</v-icon>
                <span class="text-capitalize mr-3">{{ item.section }}</span>
                <v-icon x-small class="mr-1">mdi-calendar-blank</v-icon>
                <span class="mr-3">{{ formatDate(item.date) }}</span>
              </div>
              <div class="subtitle-1 font-weight-medium finding__title">
                {{ item.title }}
              </div>
              <div
                class="
                  body-2
                  grey--text
                  text--darken-1
                  mt-1
                  d-flex
                  align-center
                  flex-wrap
                "
                v-if="serviceNames(item).length"
              >
                <span
                  v-for="(n, i) in serviceNames(item)"
                  :key="i"
                  class="service-pill mr-2 mt-1"
                >
                  {{ n }}
                </span>
              </div>
            </div>
            <div class="d-flex align-center ml-4 finding__actions">
              <v-chip
                v-if="updated.includes(item.id)"
                small
                outlined
                color="success"
                class="mr-2"
              >
                <v-icon x-small left>mdi-check</v-icon>Times updated
              </v-chip>
              <v-btn
                v-else-if="action(item)"
                small
                text
                color="primary"
                class="mr-1"
                @click.stop="runAction(item)"
              >
                <v-icon small left>{{ action(item).icon }}</v-icon>
                {{ action(item).label }}
              </v-btn>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn icon small v-on="on" @click.stop="dismiss(item)">
                    <v-icon small>mdi-eye-off-outline</v-icon>
                  </v-btn>
                </template>
                <span>Dismiss (until the next run)</span>
              </v-tooltip>
              <v-btn icon small>
                <v-icon>{{
                  isExpanded(item) ? "mdi-chevron-up" : "mdi-chevron-down"
                }}</v-icon>
              </v-btn>
            </div>
          </div>

          <v-expand-transition>
            <div v-show="isExpanded(item)">
              <v-divider></v-divider>
              <div class="px-5 py-5">
                <div class="body-1 finding__detail">{{ item.detail }}</div>

                <div
                  v-if="item.candidates && item.candidates.length"
                  class="mt-5"
                >
                  <div class="overline grey--text text--darken-1 mb-2">
                    Alternatives
                  </div>
                  <div class="candidates">
                    <v-simple-table dense>
                      <thead>
                        <tr>
                          <th>Supplier</th>
                          <th>Service</th>
                          <th class="text-right">Cost</th>
                          <th class="text-right">Sell</th>
                          <th class="text-right">Margin Δ</th>
                          <th>Source</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(c, i) in item.candidates" :key="i">
                          <td class="font-weight-medium">
                            {{ c.supplierName }}
                          </td>
                          <td>{{ c.serviceName }}</td>
                          <td class="text-right">{{ amount(c.costBase) }}</td>
                          <td class="text-right">{{ amount(c.sellBase) }}</td>
                          <td
                            class="text-right font-weight-medium"
                            :class="c.marginDelta > 0 ? 'success--text' : ''"
                          >
                            {{ c.marginDelta > 0 ? "+" : ""
                            }}{{ amount(c.marginDelta) }}
                          </td>
                          <td>
                            <v-chip
                              x-small
                              outlined
                              :color="c.source === 'local' ? 'primary' : 'grey'"
                            >
                              {{ c.source }}
                            </v-chip>
                          </td>
                          <td class="text-right">
                            <v-tooltip
                              bottom
                              v-if="
                                canEdit && c.source === 'local' && c.serviceId
                              "
                            >
                              <template v-slot:activator="{ on }">
                                <v-btn
                                  x-small
                                  outlined
                                  color="primary"
                                  v-on="on"
                                  @click="swap(item, c)"
                                >
                                  Swap
                                </v-btn>
                              </template>
                              <span
                                >Open the builder with this service
                                selected</span
                              >
                            </v-tooltip>
                          </td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                  </div>
                </div>

                <div class="mt-5">
                  <v-btn
                    text
                    x-small
                    color="grey darken-1"
                    class="px-0"
                    @click="toggleEvidence(item)"
                  >
                    <v-icon x-small left>
                      {{
                        evidenceOpen.includes(item.id)
                          ? "mdi-chevron-up"
                          : "mdi-chevron-down"
                      }}
                    </v-icon>
                    Evidence
                  </v-btn>
                  <v-btn
                    v-if="explainers[item.code]"
                    text
                    x-small
                    color="grey darken-1"
                    class="ml-3 px-0"
                    @click="explainer = explainers[item.code]"
                  >
                    <v-icon x-small left>mdi-information-outline</v-icon>
                    How is this picked?
                  </v-btn>
                  <v-expand-transition>
                    <pre
                      v-show="evidenceOpen.includes(item.id)"
                      class="caption evidence-json mt-2 pa-3"
                      >{{ pretty(item.evidence) }}</pre
                    >
                  </v-expand-transition>
                </div>
              </div>
            </div>
          </v-expand-transition>
        </v-card>
      </div>

      <v-card
        v-if="filteredFindings.length === 0"
        outlined
        class="soft-card pa-8 text-center grey--text"
      >
        No findings match the current filters.
      </v-card>

      <div v-if="dismissedCount" class="caption grey--text text-center mt-2">
        {{ dismissedCount }} dismissed
        <v-btn x-small text color="primary" @click="restoreDismissed()">
          Show again
        </v-btn>
      </div>
    </template>

    <v-dialog
      :value="!!timesFor"
      @input="closeTimes()"
      max-width="640"
      persistent
    >
      <v-card v-if="timesFor">
        <v-card-title class="title">Set times</v-card-title>
        <v-card-subtitle class="pt-1">{{ timesFor.title }}</v-card-subtitle>
        <v-card-text>
          <div v-for="row in timesRows" :key="row.id" class="mb-4">
            <div class="d-flex align-center">
              <span class="subtitle-2">{{ row.label }}</span>
              <v-spacer></v-spacer>
              <v-btn
                x-small
                text
                color="grey darken-1"
                title="Change the date in the builder"
                @click="openBuilder({ action: 'edit', item: row.id })"
              >
                <v-icon x-small left>mdi-calendar-edit</v-icon>Change date
              </v-btn>
            </div>
            <div class="caption grey--text">{{ formatDate(row.date) }}</div>
            <v-row dense class="mt-1">
              <v-col cols="6" v-for="key in timeFields" :key="key">
                <v-text-field
                  v-model="row.values[key]"
                  :label="row.labels[key]"
                  type="time"
                  dense
                  outlined
                  clearable
                  hide-details
                ></v-text-field>
              </v-col>
            </v-row>
          </div>
          <v-alert v-if="timesError" dense outlined type="error" class="mb-0">
            {{ timesError }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <span class="caption grey--text ml-2">
            Saves these times only. Prices are not recalculated.
          </span>
          <v-spacer></v-spacer>
          <v-btn text :disabled="timesSaving" @click="closeTimes()"
            >Cancel</v-btn
          >
          <v-btn
            color="primary"
            depressed
            :loading="timesSaving"
            :disabled="!timesChanged.length"
            @click="saveTimes()"
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog :value="!!explainer" @input="explainer = null" max-width="560">
      <v-card v-if="explainer">
        <v-card-title class="title">{{ explainer.title }}</v-card-title>
        <v-card-text class="body-2">
          <p v-for="(line, i) in explainer.lines" :key="i" class="mb-3">
            <span class="font-weight-medium">{{ line.label }}</span>
            {{ line.text }}
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="explainer = null">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {
  ITINERARY_ANALYSIS,
  REQUEST_ITINERARY_ANALYSIS,
  UPDATE_SERVICE_REMARKS,
} from "../../../../constants/graphql";
const SEVERITIES = ["critical", "warning", "info"];
const POLL_INTERVAL = 2000;
const parseEvidence = (str) => {
  if (!str) return {};
  try {
    return JSON.parse(str);
  } catch (e) {
    return { raw: str };
  }
};
const moment = require("moment");
// findings whose fix is a time on the item: set inline (updateServiceRemarks)
const TIME_CODES = ["TIME_CLASH", "TIME_MISSING"];
// The item's own time fields, labelled as in the builder (ItemRemarks): an activity's
// start/end are its pickup/dropoff times. time_from is an accommodation field.
const TIME_FIELDS = ["pickupTime", "dropoffTime"];
const timeLabels = (serviceType) =>
  serviceType === "ACTIVITY"
    ? { pickupTime: "Start time", dropoffTime: "End time" }
    : { pickupTime: "Pickup time", dropoffTime: "Dropoff time" };
// Dismissed finding ids per run, this browser only; a new run has a new key
const DISMISS_KEY = "gt-ita-dismissed-";
const readDismissed = (analysisId) => {
  try {
    return JSON.parse(localStorage.getItem(DISMISS_KEY + analysisId)) || [];
  } catch (e) {
    return [];
  }
};
const writeDismissed = (analysisId, ids) => {
  try {
    if (ids.length)
      localStorage.setItem(DISMISS_KEY + analysisId, JSON.stringify(ids));
    else localStorage.removeItem(DISMISS_KEY + analysisId);
  } catch (e) {
    // private window or storage blocked: dismiss lasts for this page only
  }
};
// "Service-123" global id -> "123"
const rawId = (gid) => {
  try {
    return atob(gid).split("-").pop();
  } catch (e) {
    return null;
  }
};
// "How is this picked?" popups, per finding code. Keep in step with the check in
// gtweb app/models/itinerary_analysis/checks/.
const EXPLAINERS = {
  ACTIVITY_PARTY_MISMATCH: {
    title: "How party fit is checked",
    lines: [
      {
        label: "Who:",
        text: "travellers under 18 and 65 or over, from participant ages. Without ages, children on the booking count; without those, the participant notes are read.",
      },
      {
        label: "Which activities:",
        text: "activities with a description of their own or of the venue. An activity with only a name is not judged.",
      },
      {
        label: "Flagged:",
        text: "as Critical when the activity looks adults-only and someone is under 18. As Warning when it looks strenuous or needs full mobility and someone is 65 or over. As Info when it is unclear.",
      },
      {
        label: "Judged by:",
        text: "an AI model reading the activity description. Please check the activity yourself before changing the booking.",
      },
    ],
  },
  TIME_MISSING: {
    title: "How missing times are picked",
    lines: [
      {
        label: "Which items:",
        text: "every activity, and every transfer that starts and ends on the same day, quotes included. Extras, optional and cancelled items, package components and multi-day transfers are left out.",
      },
      {
        label: "Flagged:",
        text: "as Warning when the remarks have neither a pickup time nor a start time. Any text in either field counts as a time.",
      },
    ],
  },
  MARGIN_BELOW_TARGET: {
    title: "How margin items are picked",
    lines: [
      {
        label: "Which items:",
        text: "every item with both a cost and a sell price. Optional, cancelled and not-included items, package components and cancellation fees are left out.",
      },
      {
        label: "Margin:",
        text: "(sell − cost − agent commission) ÷ sell, in the itinerary's base currency.",
      },
      {
        label: "Target:",
        text: "if a markup applies for this itinerary's agent (markup table or the agent's finance settings), the margin that markup would give. Otherwise half of the itinerary's own overall margin.",
      },
      {
        label: "Flagged:",
        text: "as Info when the item is more than 0.5 points below its target (1 point for extras). As Warning when it is sold below cost, or has a cost and no sell price.",
      },
      {
        label: "Shown:",
        text: "how many points and how much money the item is short of its target.",
      },
    ],
  },
};

export default {
  name: "itinerary-analysis-tab",

  props: {
    itinerary: { type: Object, required: true },
  },

  data() {
    return {
      analysis: null,
      starting: false,
      requestMessage: null,
      expanded: [],
      evidenceOpen: [],
      explainers: EXPLAINERS,
      explainer: null,
      dismissed: [],
      updated: [],
      timesFor: null,
      timesRows: [],
      timesSaving: false,
      timesError: null,
      timeFields: TIME_FIELDS,
      severityFilter: [...SEVERITIES],
      sectionFilter: [],
      severities: SEVERITIES,
      // Phase 1 checks only
      checkChips: [
        { label: "Gap nights", icon: "mdi-bed", color: "error" },
        { label: "Overlapping stays", icon: "mdi-bed-double", color: "error" },
        { label: "Missing transfers", icon: "mdi-car", color: "warning" },
        {
          label: "Time clashes",
          icon: "mdi-clock-alert-outline",
          color: "warning",
        },
        {
          label: "Missing times",
          icon: "mdi-clock-outline",
          color: "warning",
        },
        { label: "Pax mismatch", icon: "mdi-account-group", color: "warning" },
        {
          label: "Party fit",
          icon: "mdi-human-cane",
          color: "warning",
        },
        { label: "Margin below target", icon: "mdi-cash", color: "info" },
        {
          label: "Cheaper alternatives",
          icon: "mdi-swap-horizontal",
          color: "info",
        },
      ],
    };
  },

  apollo: {
    analysis: {
      query: ITINERARY_ANALYSIS,
      variables() {
        return { itineraryId: this.itinerary.id };
      },
      fetchPolicy: "network-only",
      update(data) {
        const a = data.itineraryAnalysis;
        if (!a) return null;
        return {
          ...a,
          findings: (a.findings || []).map((f) => ({
            ...f,
            evidence: parseEvidence(f.evidence),
          })),
        };
      },
      result() {
        this.syncPolling();
        this.dismissed = this.analysis ? readDismissed(this.analysis.id) : [];
      },
    },
  },

  computed: {
    inProgress() {
      return (
        !!this.analysis &&
        (this.analysis.status === "queued" ||
          this.analysis.status === "running")
      );
    },
    findings() {
      return ((this.analysis && this.analysis.findings) || []).filter(
        (f) => !this.dismissed.includes(f.id)
      );
    },
    dismissedCount() {
      return this.analysis ? this.dismissed.length : 0;
    },
    canEdit() {
      return this.$store.getters.hasAccessTo("itineraries:edit");
    },
    timesChanged() {
      return this.timesRows
        .map((r) => {
          const remarks = {};
          TIME_FIELDS.forEach((key) => {
            const v = r.values[key] || "";
            if (v !== (r.original[key] || "")) remarks[key] = v;
          });
          return { groupId: r.id, remarks };
        })
        .filter((s) => Object.keys(s.remarks).length);
    },
    counts() {
      const c = { critical: 0, warning: 0, info: 0 };
      this.findings.forEach((f) => (c[f.severity] += 1));
      return c;
    },
    sectionsPresent() {
      return [...new Set(this.findings.map((f) => f.section))];
    },
    filteredFindings() {
      return this.findings.filter(
        (f) =>
          this.severityFilter.includes(f.severity) &&
          (this.sectionFilter.length === 0 ||
            this.sectionFilter.includes(f.section))
      );
    },
    groupedFindings() {
      return SEVERITIES.map((severity) => ({
        severity,
        items: this.filteredFindings.filter((f) => f.severity === severity),
      })).filter((g) => g.items.length > 0);
    },
    narrative() {
      return !!(this.analysis.stats && this.analysis.stats.narrativeModel);
    },
    recommendations() {
      return (this.analysis && this.analysis.recommendations) || [];
    },
    findingsById() {
      const map = {};
      ((this.analysis && this.analysis.findings) || []).forEach(
        (f) => (map[f.id] = f)
      );
      return map;
    },
    itemsById() {
      const edges =
        this.itinerary && this.itinerary.items && this.itinerary.items.edges
          ? this.itinerary.items.edges
          : [];
      const map = {};
      edges.forEach((e) => (map[e.node.id] = e.node));
      return map;
    },
  },

  methods: {
    analyse() {
      if (this.starting || this.inProgress) return;
      this.starting = true;
      this.requestMessage = null;
      this.expanded = [];
      this.evidenceOpen = [];
      this.updated = [];
      this.$apollo
        .mutate({
          mutation: REQUEST_ITINERARY_ANALYSIS,
          variables: { itineraryId: this.itinerary.id },
        })
        .then(({ data }) => {
          const r = data.requestItineraryAnalysis;
          if (r && r.status === "error") {
            this.requestMessage = {
              type: "error",
              text: r.message || "The analysis could not be started.",
            };
          } else if (r && r.status === "refused") {
            // an earlier run is still queued/running: just follow it
            this.requestMessage = {
              type: "info",
              text: r.message || "An analysis is already in progress.",
            };
          }
          return this.$apollo.queries.analysis.refetch();
        })
        .catch((e) => {
          this.requestMessage = {
            type: "error",
            text: (e && e.message) || "The analysis could not be started.",
          };
        })
        .finally(() => {
          this.starting = false;
        });
    },
    syncPolling() {
      const q = this.$apollo.queries.analysis;
      if (!q) return;
      if (this.inProgress) q.startPolling(POLL_INTERVAL);
      else q.stopPolling();
    },
    isExpanded(item) {
      return this.expanded.includes(item.id);
    },
    toggleRow(item) {
      const i = this.expanded.indexOf(item.id);
      if (i >= 0) this.expanded.splice(i, 1);
      else this.expanded.push(item.id);
    },
    findingTitle(id) {
      const f = this.findingsById[id];
      return f ? f.title : id;
    },
    // shows the finding even when the filters hide it, opens and scrolls to it
    openFinding(id) {
      const f = this.findingsById[id];
      if (!f) return;
      if (this.dismissed.includes(id))
        this.setDismissed(this.dismissed.filter((d) => d !== id));
      if (!this.severityFilter.includes(f.severity))
        this.severityFilter.push(f.severity);
      if (this.sectionFilter.length && !this.sectionFilter.includes(f.section))
        this.sectionFilter.push(f.section);
      if (!this.expanded.includes(id)) this.expanded.push(id);
      this.$nextTick(() => {
        const el = document.getElementById("finding-" + id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    },
    toggleEvidence(item) {
      const i = this.evidenceOpen.indexOf(item.id);
      if (i >= 0) this.evidenceOpen.splice(i, 1);
      else this.evidenceOpen.push(item.id);
    },
    toggleSeverity(s) {
      const i = this.severityFilter.indexOf(s);
      if (i >= 0) this.severityFilter.splice(i, 1);
      else this.severityFilter.push(s);
    },
    serviceNames(item) {
      return (item.serviceIds || [])
        .map((id) => this.itemsById[id])
        .filter(Boolean)
        .map((n) =>
          [n.supplierName, n.serviceName].filter(Boolean).join(" — ")
        );
    },
    severityColor(s) {
      return (
        { critical: "error", warning: "warning", info: "info" }[s] || "grey"
      );
    },
    severityLabel(s) {
      return (
        { critical: "Critical", warning: "Warnings", info: "Suggestions" }[s] ||
        s
      );
    },
    severityIcon(s) {
      return (
        {
          critical: "mdi-alert-octagon",
          warning: "mdi-alert",
          info: "mdi-lightbulb-on-outline",
        }[s] || "mdi-help-circle"
      );
    },
    sectionIcon(s) {
      return (
        {
          accommodation: "mdi-bed",
          transfer: "mdi-car",
          activity: "mdi-ticket",
          transport: "mdi-airplane",
          pricing: "mdi-cash",
          party: "mdi-account-group",
        }[s] || "mdi-tag"
      );
    },
    // #11503 ITA-22 one-click actions. Builder actions only open a dialog there;
    // times are the one thing saved from here (updateServiceRemarks).
    action(item) {
      if (!this.canEdit) return null;
      if (TIME_CODES.includes(item.code))
        return {
          label:
            item.suggestionType === "reschedule" ? "Reschedule" : "Set times",
          icon: this.suggestionIcon("reschedule"),
        };
      if (item.suggestionType === "add_transfer")
        return {
          label: "Add transfer",
          icon: this.suggestionIcon("add_transfer"),
        };
      if (
        item.suggestionType === "swap_service" &&
        (item.candidates || []).some((c) => c.source === "local" && c.serviceId)
      )
        return {
          label: "Swap service",
          icon: this.suggestionIcon("swap_service"),
        };
      return null;
    },
    runAction(item) {
      if (TIME_CODES.includes(item.code)) this.openTimes(item);
      else if (item.suggestionType === "add_transfer") this.addTransfer(item);
      else if (!this.isExpanded(item)) this.toggleRow(item); // candidates carry the Swap buttons
    },
    openBuilder(query) {
      this.$router.push({
        path: "/itinerary/" + this.itinerary.id + "/edit",
        query,
      });
    },
    addTransfer(item) {
      const ev = item.evidence || {};
      const query = { action: "add_transfer", date: item.date };
      if (ev.reason === "flight_arrival") query.from = "Airport";
      else if (ev.reason === "flight_departure") query.to = "Airport";
      else if (!/^flight/.test(ev.reason || "")) {
        query.from = ev.from_supplier || "";
        query.to = ev.to_supplier || "";
      }
      if (ev.flight_no) query.flight = ev.flight_no;
      this.openBuilder(query);
    },
    swap(item, candidate) {
      const serviceRawId = rawId(candidate.serviceId);
      const ev = ((item.evidence || {}).candidates || []).find(
        (c) => String(c.service_id) === serviceRawId
      );
      const query = {
        action: "swap",
        item: (item.serviceIds || [])[0],
        service: candidate.serviceId,
        supplierName: candidate.supplierName,
      };
      if (ev && ev.supplier_id)
        query.supplier = btoa("Supplier-" + ev.supplier_id);
      this.openBuilder(query);
    },
    openTimes(item) {
      this.timesError = null;
      this.timesRows = (item.serviceIds || [])
        .map((id) => this.itemsById[id])
        .filter(Boolean)
        .map((n) => {
          const r = n.remarks || {};
          const original = {};
          TIME_FIELDS.forEach((key) => (original[key] = r[key] || ""));
          return {
            id: n.id,
            label: [n.supplierName, n.serviceName].filter(Boolean).join(" — "),
            date: n.startDate,
            labels: timeLabels(n.serviceTypeName),
            original,
            values: { ...original },
          };
        });
      if (!this.timesRows.length) {
        this.requestMessage = {
          type: "info",
          text: "The items of this finding have changed since the analysis ran. Please run it again.",
        };
        return;
      }
      this.timesFor = item;
    },
    closeTimes() {
      if (this.timesSaving) return;
      this.timesFor = null;
      this.timesRows = [];
    },
    saveTimes() {
      const finding = this.timesFor;
      this.timesSaving = true;
      this.timesError = null;
      this.$apollo
        .mutate({
          mutation: UPDATE_SERVICE_REMARKS,
          variables: { id: this.itinerary.id, services: this.timesChanged },
        })
        .then(() => {
          this.updated.push(finding.id);
          this.timesSaving = false;
          this.closeTimes();
          this.$emit("itinerary-changed");
        })
        .catch((e) => {
          this.timesSaving = false;
          this.timesError = (
            (e && e.message) ||
            "The times could not be saved."
          ).replace(/^GraphQL error: /, "");
        });
    },
    dismiss(item) {
      this.setDismissed([...this.dismissed, item.id]);
    },
    restoreDismissed() {
      this.setDismissed([]);
    },
    setDismissed(ids) {
      this.dismissed = ids;
      writeDismissed(this.analysis.id, ids);
    },
    suggestionIcon(t) {
      return (
        {
          add_transfer: "mdi-car-plus",
          swap_service: "mdi-swap-horizontal",
          reschedule: "mdi-calendar-clock",
          review: "mdi-eye-outline",
        }[t] || "mdi-eye-outline"
      );
    },
    suggestionLabel(t) {
      return (
        {
          add_transfer: "Add transfer",
          swap_service: "Swap service",
          reschedule: "Reschedule",
          review: "Review",
        }[t] || t
      );
    },
    amount(v) {
      if (v === null || v === undefined) return "—";
      return `${Number(v).toFixed(2)} ${this.itinerary.currencyCode || ""}`;
    },
    pretty(obj) {
      return JSON.stringify(obj || {}, null, 2);
    },
    formatDate(d) {
      if (!d) return "—";
      return moment(d).format(this.$store.getters.userConfig.dateFormat);
    },
    formatDateTime(d) {
      if (!d) return "";
      return moment(d).format(
        `${this.$store.getters.userConfig.dateFormat} HH:mm`
      );
    },
  },

  beforeDestroy() {
    const q = this.$apollo.queries.analysis;
    if (q) q.stopPolling();
  },
};
</script>

<style scoped>
.itinerary-analysis-tab {
  max-width: 1400px;
}
.soft-card {
  border-radius: 12px !important;
  border-color: rgba(0, 0, 0, 0.08) !important;
}
.analyse-btn {
  border-radius: 8px;
  letter-spacing: 0.5px;
}
.min-w-0 {
  min-width: 0;
}

.narrative-summary {
  line-height: 1.6;
}

/* AI recommendations */
.recommendations {
  padding-left: 20px;
}
.finding-link {
  display: inline-block;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
  padding: 0 8px;
  border-radius: 10px;
  background-color: rgba(0, 0, 0, 0.05);
  cursor: pointer;
}
.finding-link:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

/* summary stat tiles */
.stat-tile {
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  cursor: pointer;
  user-select: none;
  transition: background-color 0.15s, box-shadow 0.15s, opacity 0.15s;
  background-color: #fff !important;
}
.stat-tile:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
.stat-tile--on.stat-tile--critical {
  background-color: #fff5f5 !important;
  border-color: rgba(244, 67, 54, 0.35);
}
.stat-tile--on.stat-tile--warning {
  background-color: #fff8ee !important;
  border-color: rgba(251, 140, 0, 0.35);
}
.stat-tile--on.stat-tile--info {
  background-color: #f1f7fe !important;
  border-color: rgba(33, 150, 243, 0.35);
}
.stat-tile--off {
  opacity: 0.55;
}

/* finding cards */
.finding {
  border-left-width: 4px !important;
  transition: box-shadow 0.15s;
}
.finding--critical {
  border-left-color: #f44336 !important;
}
.finding--warning {
  border-left-color: #fb8c00 !important;
}
.finding--info {
  border-left-color: #2196f3 !important;
}
.finding:hover,
.finding--open {
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.08);
}
.finding__head {
  cursor: pointer;
}
.finding__title {
  line-height: 1.4;
}
.finding__detail {
  max-width: 900px;
  line-height: 1.6;
}
.finding__actions {
  flex-shrink: 0;
}
.service-pill {
  display: inline-block;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 12px;
}

.candidates {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  overflow: hidden;
}
.candidates >>> table {
  background: transparent;
}
.candidates >>> thead th {
  background: rgba(0, 0, 0, 0.025);
}

.evidence-json {
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  max-height: 260px;
  overflow: auto;
  background: rgba(0, 0, 0, 0.035);
  border-radius: 8px;
  font-family: "SFMono-Regular", Menlo, Consolas, monospace;
}

@media (max-width: 600px) {
  .finding__head {
    flex-direction: column;
  }
  .finding__actions {
    margin-left: 0 !important;
    margin-top: 12px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/gt/itineraries/analysis/ItineraryAnalysisTab.vue